import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';

interface ApiErrorPayload {
  error?: {
    message?: string;
  };
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const parsed = payload as ApiErrorPayload;
    const message = parsed.error?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

interface FuelTypeRecord {
  id?: unknown;
  name?: unknown;
}

function isFuelTypeRecord(value: unknown): value is Required<FuelTypeRecord> {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as FuelTypeRecord;
  return (
    (typeof record.id === 'number' || typeof record.id === 'string') &&
    typeof record.name === 'string'
  );
}

export interface FuelTypeOption {
  id: string;
  name: string;
}

async function fetchFuelTypes(): Promise<FuelTypeOption[]> {
  const response = await apiFetch('/api/v1/fuel-types');
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(
      payload,
      `Failed to load fuel types: ${response.status} ${response.statusText}`
    );
    throw new Error(message);
  }

  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.filter(isFuelTypeRecord).map((item) => ({
    id: String(item.id),
    name: String(item.name),
  }));
}

export function useFuelTypes() {
  return useQuery({
    queryKey: queryKeys.fuelTypes,
    queryFn: fetchFuelTypes,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });
}

export { fetchFuelTypes };
